import { useState, FormEvent, ChangeEvent } from "react";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";

export default function Contact() {
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [fileName, setFileName] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");

    const form = e.currentTarget;
    const formData = new FormData(form);

    // Honeypot check
    const honeypot = formData.get("company_alt");
    if (honeypot && String(honeypot).trim() !== "") {
      setFormStatus("success");
      return;
    }

    // Remove honeypot from submission
    formData.delete("company_alt");

    try {
      const response = await fetch("https://readdy.ai/api/form/d8tb4d4al24muhn2r5t0", {
        method: "POST",
        body: formData,
      });

      if (response.ok) {
        setFormStatus("success");
        form.reset();
        setFileName("");
      } else {
        setFormStatus("error");
      }
    } catch {
      setFormStatus("error");
    }
  };

  const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    setFileName(file ? file.name : "");
  };

  return (
    <div className="min-h-screen bg-background-50">
      <Navbar />
      <main>
        {/* Hero Header */}
        <section className="pt-28 md:pt-32 pb-12 md:pb-16 px-4 md:px-6 lg:px-10 bg-background-100">
          <div className="max-w-7xl mx-auto text-center">
            <span className="inline-block text-xs font-semibold uppercase tracking-wider text-accent-500 border border-accent-200 rounded-full px-3 py-1 mb-4">
              Contact Us
            </span>
            <h1 className="font-heading text-3xl md:text-4xl lg:text-5xl font-bold text-foreground-950 leading-tight mb-4">
              Get in <span className="text-primary-500">Touch</span>
            </h1>
            <p className="text-foreground-600 text-base md:text-lg max-w-xl mx-auto">
              Upload your fire safety documents or send us an inquiry. Our SCDF-certified team will respond within 2 business days.
            </p>
          </div>
        </section>

        {/* Contact Grid */}
        <section className="px-4 md:px-6 lg:px-10 py-12 md:py-16 max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-12">
            {/* Left: Contact Info */}
            <div className="lg:col-span-2">
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-6">
                Contact Information
              </h2>
              <div className="flex flex-col gap-6">
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-map-pin-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-1">Address</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      Blk 15 Kaki Bukit Road 4<br />
                      #01-44 Bartley Biz Centre<br />
                      Singapore 417808
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-whatsapp-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-1">WhatsApp</h3>
                    <a href="https://wa.me/6585353868" target="_blank" rel="noopener noreferrer" className="text-foreground-600 text-sm hover:text-primary-500 transition-colors">
                      +65 8535 3868
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-mail-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-1">Email</h3>
                    <a href="mailto:david@ezzogenics.com" className="text-foreground-600 text-sm hover:text-primary-500 transition-colors">
                      david@ezzogenics.com
                    </a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0">
                    <i className="ri-time-line text-primary-600 text-lg"></i>
                  </div>
                  <div>
                    <h3 className="font-semibold text-foreground-900 text-sm mb-1">Business Hours</h3>
                    <p className="text-foreground-600 text-sm leading-relaxed">
                      Monday – Friday: 9:00 AM – 6:00 PM<br />
                      Saturday: 9:00 AM – 1:00 PM<br />
                      Sunday: Closed
                    </p>
                  </div>
                </div>
              </div>

              {/* Certifications */}
              <div className="mt-8 p-5 bg-background-100 rounded-2xl border border-background-200/70">
                <h3 className="font-semibold text-foreground-900 text-sm mb-3">Our Certifications</h3>
                <div className="flex flex-wrap gap-2">
                  <span className="px-3 py-1.5 bg-secondary-100 text-secondary-900 text-xs font-medium rounded-full">SCDF Registered</span>
                  <span className="px-3 py-1.5 bg-secondary-100 text-secondary-900 text-xs font-medium rounded-full">BCA Licensed</span>
                  <span className="px-3 py-1.5 bg-secondary-100 text-secondary-900 text-xs font-medium rounded-full">BizSAFE Level 3</span>
                  <span className="px-3 py-1.5 bg-secondary-100 text-secondary-900 text-xs font-medium rounded-full">ISO 9001</span>
                </div>
              </div>

              {/* Map */}
              <div className="mt-8 rounded-2xl overflow-hidden h-52 md:h-64 border border-background-200/70">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3988.7549!2d103.9080!3d1.3348!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x31da17b3a8e3d3b7%3A0x3c2e3c3c3c3c3c3c!2sBartley%20Biz%20Centre%2C%20Kaki%20Bukit%20Road%204%2C%20Singapore!5e0!3m2!1sen!2ssg!4v1700000000000!5m2!1sen!2ssg"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Ezzogenics Office Location at Bartley Biz Centre, Kaki Bukit"
                  aria-label="Google Map showing Ezzogenics office at Blk 15 Kaki Bukit Road 4 Bartley Biz Centre"
                ></iframe>
              </div>
            </div>

            {/* Right: Form */}
            <div className="lg:col-span-3">
              <div className="bg-background-50 rounded-2xl border border-background-200/70 p-6 md:p-8 lg:p-10">
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">
                  Document Upload & Inquiry
                </h2>
                <p className="text-foreground-600 text-sm mb-6">
                  Fill in the form below to upload your fire safety documents or request a quote. We accept PDF, DWG, and image files.
                </p>

                {formStatus === "success" && (
                  <div className="mb-6 p-4 bg-accent-100 rounded-xl border border-accent-200">
                    <div className="flex items-center gap-2">
                      <i className="ri-check-line text-accent-600 text-xl"></i>
                      <p className="text-accent-900 text-sm font-medium">
                        Thank you! Your submission has been received. We will review your documents and respond within 2 business days.
                      </p>
                    </div>
                  </div>
                )}

                {formStatus === "error" && (
                  <div className="mb-6 p-4 bg-primary-100 rounded-xl border border-primary-200">
                    <div className="flex items-center gap-2">
                      <i className="ri-error-warning-line text-primary-600 text-xl"></i>
                      <p className="text-primary-900 text-sm font-medium">
                        Something went wrong. Please try again or contact us on WhatsApp at +65 8535 3868.
                      </p>
                    </div>
                  </div>
                )}

                <form
                  id="fire-safety-upload-form"
                  data-readdy-form
                  onSubmit={handleSubmit}
                  className="flex flex-col gap-5"
                >
                  {/* Honeypot */}
                  <div className="hp-field">
                    <input
                      type="text"
                      name="company_alt"
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                    />
                  </div>

                  {/* Name & Email Row */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        Full Name <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="John Tan"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        Email Address <span className="text-primary-500">*</span>
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="john@company.com"
                      />
                    </div>
                  </div>

                  {/* Phone & Company */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        Phone Number
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="+65-XXXX-XXXX"
                      />
                    </div>
                    <div>
                      <label htmlFor="company" className="block text-sm font-medium text-foreground-800 mb-1.5">
                        Company / Organization
                      </label>
                      <input
                        type="text"
                        id="company"
                        name="company"
                        className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all"
                        placeholder="ABC Pte Ltd"
                      />
                    </div>
                  </div>

                  {/* Service Type */}
                  <div>
                    <label htmlFor="service_type" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      Service Required
                    </label>
                    <select
                      id="service_type"
                      name="service_type"
                      className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all appearance-none"
                    >
                      <option value="">Select a service...</option>
                      <option value="fire-risk-assessment">Fire Risk Assessment</option>
                      <option value="fire-alarm-systems">Fire Alarm Systems</option>
                      <option value="fire-suppression">Fire Suppression Systems</option>
                      <option value="emergency-lighting">Emergency Lighting</option>
                      <option value="scdf-compliance">SCDF Compliance Consulting</option>
                      <option value="document-review">Document Review & Approval</option>
                      <option value="other">Other</option>
                    </select>
                  </div>

                  {/* Message */}
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      Message / Details
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      maxLength={500}
                      className="w-full px-4 py-3 rounded-xl border border-background-200/70 bg-background-50 text-foreground-950 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 focus:border-primary-300 transition-all resize-none"
                      placeholder="Describe your project or inquiry..."
                    ></textarea>
                    <p className="text-foreground-400 text-xs mt-1">Maximum 500 characters</p>
                  </div>

                  {/* File Upload */}
                  <div>
                    <label htmlFor="documents" className="block text-sm font-medium text-foreground-800 mb-1.5">
                      Upload Documents
                    </label>
                    <div className="relative">
                      <input
                        type="file"
                        id="documents"
                        name="documents"
                        accept=".pdf,.dwg,.jpg,.jpeg,.png,.doc,.docx"
                        onChange={handleFileChange}
                        className="hidden"
                      />
                      <label
                        htmlFor="documents"
                        className="flex items-center justify-center gap-3 w-full px-4 py-4 rounded-xl border-2 border-dashed border-background-300 hover:border-primary-400 bg-background-100 cursor-pointer transition-all"
                      >
                        <i className="ri-upload-cloud-2-line text-primary-500 text-xl"></i>
                        <span className="text-foreground-600 text-sm">
                          {fileName || "Click to upload PDF, DWG, or image files"}
                        </span>
                      </label>
                    </div>
                    <p className="text-foreground-400 text-xs mt-1.5">
                      Accepted: PDF, DWG, JPG, PNG, DOC (max 10MB)
                    </p>
                  </div>

                  {/* Submit Button */}
                  <button
                    type="submit"
                    disabled={formStatus === "submitting"}
                    className="inline-flex items-center justify-center gap-2 w-full px-6 py-4 rounded-xl bg-primary-500 text-background-50 text-sm md:text-base font-semibold hover:bg-primary-600 transition-colors disabled:opacity-70 disabled:cursor-not-allowed whitespace-nowrap"
                  >
                    {formStatus === "submitting" ? (
                      <>
                        <i className="ri-loader-4-line animate-spin text-lg"></i>
                        Submitting...
                      </>
                    ) : (
                      <>
                        Submit Inquiry
                        <i className="ri-send-plane-line text-lg"></i>
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}